import type { QueryObserverResult } from '@tanstack/query-core'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { useEffect } from 'react'
import type { EnvType } from '~/env'
import { listenReserveSuccess$ } from '~/features/reservationInput/store/reserveSuccess'
import type { DiscountCouponCode, PlanId } from '~/lib/graphql/scalars'
import type { ReserveInputContext } from '~/navigation'
import {
  listenValidateCouponQuery,
  maxDiscountCoupon$,
  popupCouponDisplayed$,
} from './discountCoupon'
import { listenReserveError$, useInitScopedStore } from './error'
import { disabledOnsite } from './lib/checkDisabled'
import { initialGivenPointsType } from './payment'
import { listenCardRegistration$, listenRegisteringMember$, listenReserveReady$ } from './reserve'
import type {
  ReserveInputCards,
  ReserveInputMember,
  ReserveInputPlan,
  ReserveInputReserveInfo,
  ReserveInputRestaurant,
  ReserveInputSeat,
  ReserveInputSeats,
  ReserveInputViewer,
} from './stateMachine/state'
import {
  cards$,
  listenInitEvent$,
  member$,
  navigationContext$,
  plan$,
  reserveInfo$,
  reserveInfoQueryResult$,
  restaurant$,
  seats$,
  transition$,
  viewer$,
} from './stateMachine/state'
import type { ReservationInputEvent, SelectedDiscountCouponType } from './stateMachine/statemachine'
import { listenTrustedGuest$ } from './trustedGuest'
import { listenTrustedMember$ } from './trustedMember'
import { listenUserInputError$ } from './validate'
import { listenYahooMemberLinking$ } from './yahooMemberRelation'

function isGuestUser(member: ReserveInputMember, viewer: ReserveInputViewer) {
  return !member || !viewer
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isGuestUser', () => {
    expect(isGuestUser(undefined as ReserveInputMember, undefined as ReserveInputViewer)).toBe(true)
    expect(
      isGuestUser(undefined as ReserveInputMember, { name: 'test' } as ReserveInputViewer)
    ).toBe(true)
    expect(
      isGuestUser({ name: 'test' } as ReserveInputMember, undefined as ReserveInputViewer)
    ).toBe(true)
    expect(
      isGuestUser({ name: 'test' } as ReserveInputMember, { name: 'test' } as ReserveInputViewer)
    ).toBe(false)
  })
}

const ppgListenReservationInputState$ = atomEffect((get, set) => {
  const query = get(reserveInfoQueryResult$)
  // reserveInfo が取得できず __typename: reserveError が返る場合でも 200 OK になり tanstack-query は success を返すので、
  // __typename で値を取得できたか判定し __typename: ReserveInfo であれば次の state に遷移する
  // __typename: ReserveInfoError になると reserveInfo$ で error が throw される
  const reserveInfo = get(reserveInfo$)
  if (hasData(query) && query.data?.restaurant?.plan?.reserveInfo.__typename === 'ReserveInfo') {
    const restaurant = get(restaurant$)
    const plan = get(plan$)
    const viewer = get(viewer$)
    const member = get(member$)
    const seats = get(seats$)
    const maxDiscountCoupon = get(maxDiscountCoupon$)
    const point = reserveInfo?.point
    // PPGの場合も一休レストランの場合と同様に、ゲストのときは viewer と reserveInfo.member が null になるので、
    // isGuest() でゲストかどうかを判断してよい
    const isGuest = isGuestUser(get(member$), get(viewer$))
    const myCards = get(cards$)
    const context = get(navigationContext$)
    set(
      transition$,
      paypayOnLoadEventReserveInput({
        restaurant,
        plan,
        viewer,
        member,
        seats,
        discountCouponType: maxDiscountCoupon,
        point,
        myCards,
        isGuest,
        context,
      })
    )
  }
})

function paypayOnLoadEventReserveInput(input: OnLoadEventInput): ReservationInputEvent {
  const seatId = initialSelectSeatId({ seats: input.seats, context: input.context })
  return input.isGuest
    ? {
        type: 'guestLoaded',
        seatId,
        paymentMethod: 'ONSITE',
        // ゲストの場合、獲得ポイントの種類は必ず「PayPayポイント」になる
        givenPointsType: 'PAYPAY',
      }
    : {
        type: 'memberLoaded',
        seatId,
        reserverNameKana: input.member?.nameKana ?? '',
        reserverPhoneNumber: input.member?.phoneNumber ?? '',
        email: input.member?.email ?? '',
        // 登録しているカードがある場合は、デフォルトとして一番目のカードを選択する
        card:
          input.myCards.length > 0
            ? { type: 'registered', cardId: input.myCards[0].cardId }
            : undefined,
        // 現地決済が可能な場合は現地決済をデフォルトとする
        paymentMethod: disabledOnsite(input.plan) ? 'ONLINE' : 'ONSITE',
        givenPointsType: 'PAYPAY',
        // 有効なクーポンを持っている場合は、「利用する」ボタンをデフォルトにする
        isDiscountCouponEnabled: input.discountCouponType.type === 'AcquiredCoupon',
        discountCouponType: input.discountCouponType,
        subscribeMailMagazineIkyuNews: undefined,
        subscribeMailMagazinePointCoupon: undefined,
      }
}

export const ikyuListenReservationInputState$ = atomEffect((get, set) => {
  const query = get(reserveInfoQueryResult$)
  // reserveInfo が取得できず __typename: reserveError が返る場合でも 200 OK になり tanstack-query は success を返すので、
  // __typename で値を取得できたか判定し __typename: ReserveInfo であれば次の state に遷移する
  // __typename: ReserveInfoError になると reserveInfo$ で error が throw される
  const reserveInfo = get(reserveInfo$)
  if (hasData(query) && query.data?.restaurant?.plan?.reserveInfo.__typename === 'ReserveInfo') {
    const restaurant = get(restaurant$)
    const plan = get(plan$)
    const viewer = get(viewer$)
    const member = get(member$)
    const seats = get(seats$)
    const maxDiscountCoupon = get(maxDiscountCoupon$)
    const point = reserveInfo?.point
    const isGuest = isGuestUser(get(member$), get(viewer$))
    const myCards = get(cards$)
    const context = get(navigationContext$)
    set(
      transition$,
      onLoadEventReserveInput({
        restaurant,
        plan,
        viewer,
        member,
        seats,
        discountCouponType: maxDiscountCoupon,
        point,
        myCards,
        isGuest,
        context,
      })
    )
  }
})

function hasData(query: QueryObserverResult) {
  // ゲストからログインして会員になり再フェッチする際、 query.state は 'success' から変わらないので
  // fetchStatus を見て再フェッチを判定する
  return query.status === 'success' && query.fetchStatus === 'idle'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('hasData', () => {
    expect(hasData({ status: 'success', fetchStatus: 'idle' } as QueryObserverResult)).toBe(true)
    expect(hasData({ status: 'success', fetchStatus: 'fetching' } as QueryObserverResult)).toBe(
      false
    )
    expect(hasData({ status: 'pending', fetchStatus: 'idle' } as QueryObserverResult)).toBe(false)
  })
}

type ReserveInputPoint = ReserveInputReserveInfo['point']

type OnLoadEventInput = {
  restaurant?: ReserveInputRestaurant
  plan?: ReserveInputPlan
  viewer: ReserveInputViewer
  member?: ReserveInputMember
  seats?: ReserveInputSeats
  discountCouponType: SelectedDiscountCouponType
  point?: ReserveInputPoint
  myCards: ReserveInputCards
  isGuest: boolean
  context?: ReserveInputContext
  // FIXME: 案件終了後に削除する
  env?: EnvType
}

function onLoadEventReserveInput(input: OnLoadEventInput): ReservationInputEvent {
  const seatId = initialSelectSeatId({ seats: input.seats, context: input.context })
  return input.isGuest
    ? {
        type: 'guestLoaded',
        seatId,
        paymentMethod: 'ONSITE',
        // ゲストの場合、獲得ポイントの種類は必ず「一休ポイント」になる
        givenPointsType: 'IKYU',
      }
    : {
        type: 'memberLoaded',
        seatId,
        reserverNameKana: input.member?.nameKana ?? '',
        reserverPhoneNumber: input.member?.phoneNumber ?? '',
        email: input.member?.email ?? '',
        // 登録しているカードがある場合は、デフォルトとして一番目のカードを選択する
        card:
          input.myCards.length > 0
            ? { type: 'registered', cardId: input.myCards[0].cardId }
            : undefined,
        // 現地決済が可能な場合は現地決済をデフォルトとする
        paymentMethod: disabledOnsite(input.plan) ? 'ONLINE' : 'ONSITE',
        givenPointsType: initialGivenPointsType({ viewer: input.viewer, member: input.member }),
        // 有効なクーポンを持っている場合は、「利用する」ボタンをデフォルトにする
        isDiscountCouponEnabled: input.discountCouponType.type === 'AcquiredCoupon',
        discountCouponType: input.discountCouponType,
        subscribeMailMagazineIkyuNews: input.member?.mailMagazineSubscription.restaurant ?? false,
        // 「クーポンやポイントの情報」のメルマガは point か present のどちらかでも登録されていれば true にする
        subscribeMailMagazinePointCoupon:
          (input.member?.mailMagazineSubscription.point ||
            input.member?.mailMagazineSubscription.present) ??
          false,
      }
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('onLoadEventReserveInput', () => {
    it('ゲスト', () => {
      expect(
        onLoadEventReserveInput({
          viewer: {} as ReserveInputViewer,
          seats: [{ seatId: '1' }] as ReserveInputSeats,
          discountCouponType: { type: 'InputtedCouponCode' },
          myCards: [] as ReserveInputCards,
          isGuest: true,
          context: { seatTypes: ['PRIVATE_ROOM'] } as ReserveInputContext,
        })
      ).toEqual({
        type: 'guestLoaded',
        seatId: '1',
        paymentMethod: 'ONSITE',
        givenPointsType: 'IKYU',
      })
    })
    const restaurant = {
      paypayAvailable: true,
      pointAvailable: true,
      cardPaymentAvailable: true,
    } as ReserveInputRestaurant
    const plan = {
      planId: '0000' as PlanId,
      paymentType: 'ONSITE_OR_ONLINE',
    } as ReserveInputPlan
    const viewer = {
      name: '一休太郎',
      phoneNumberCertified: true,
      loginSite: 'ikyu',
    } as ReserveInputViewer
    const member = {
      name: '一休太郎',
      nameKana: 'いっきゅうたろう',
      email: 'ikyu@ikyu.com',
      phoneNumber: '09012345678',
      mailMagazineSubscription: {
        restaurant: true,
        point: true,
        present: true,
      },
      paypay: {
        __typename: 'PaypayUnavailable',
      },
    } as ReserveInputMember
    it('会員でカード/クーポンを持っていない場合', () => {
      expect(
        onLoadEventReserveInput({
          restaurant,
          plan,
          viewer,
          member,
          seats: [{ seatId: '1' }] as ReserveInputSeats,
          discountCouponType: { type: 'InputtedCouponCode' },
          myCards: [] as ReserveInputCards,
          isGuest: false,
          context: { seatTypes: ['PRIVATE_ROOM'] } as ReserveInputContext,
        })
      ).toEqual({
        type: 'memberLoaded',
        seatId: '1',
        reserverNameKana: 'いっきゅうたろう',
        reserverPhoneNumber: '09012345678',
        email: 'ikyu@ikyu.com',
        paymentMethod: 'ONSITE',
        givenPointsType: 'IKYU',
        isDiscountCouponEnabled: false,
        discountCouponType: { type: 'InputtedCouponCode' },
        subscribeMailMagazineIkyuNews: true,
        subscribeMailMagazinePointCoupon: true,
      })
    })
    it('会員でカード/クーポンを持っている場合', () => {
      expect(
        onLoadEventReserveInput({
          restaurant,
          plan,
          viewer,
          member,
          seats: [{ seatId: '1' }] as ReserveInputSeats,
          discountCouponType: { type: 'AcquiredCoupon', code: 'test' as DiscountCouponCode },
          myCards: [{ cardId: '1' }] as ReserveInputCards,
          isGuest: false,
          context: { seatTypes: ['PRIVATE_ROOM'] } as ReserveInputContext,
        })
      ).toEqual({
        type: 'memberLoaded',
        seatId: '1',
        reserverNameKana: 'いっきゅうたろう',
        reserverPhoneNumber: '09012345678',
        email: 'ikyu@ikyu.com',
        paymentMethod: 'ONSITE',
        givenPointsType: 'IKYU',
        isDiscountCouponEnabled: true,
        discountCouponType: { type: 'AcquiredCoupon', code: 'test' as DiscountCouponCode },
        card: { type: 'registered', cardId: '1' },
        subscribeMailMagazineIkyuNews: true,
        subscribeMailMagazinePointCoupon: true,
      })
    })
    it('メルマガのテスト', () => {
      expect(
        onLoadEventReserveInput({
          restaurant,
          plan,
          viewer,
          member: {
            ...member,
            mailMagazineSubscription: {
              restaurant: false,
              point: false,
              present: false,
            },
          } as ReserveInputMember,
          seats: [{ seatId: '1' }] as ReserveInputSeats,
          discountCouponType: { type: 'InputtedCouponCode' },
          myCards: [] as ReserveInputCards,
          isGuest: false,
          context: { seatTypes: ['PRIVATE_ROOM'] } as ReserveInputContext,
        })
      ).toEqual({
        type: 'memberLoaded',
        seatId: '1',
        reserverNameKana: 'いっきゅうたろう',
        reserverPhoneNumber: '09012345678',
        email: 'ikyu@ikyu.com',
        paymentMethod: 'ONSITE',
        givenPointsType: 'IKYU',
        isDiscountCouponEnabled: false,
        discountCouponType: { type: 'InputtedCouponCode' },
        subscribeMailMagazineIkyuNews: false,
        subscribeMailMagazinePointCoupon: false,
      })
      expect(
        onLoadEventReserveInput({
          restaurant,
          plan,
          viewer,
          member: {
            ...member,
            mailMagazineSubscription: {
              restaurant: true,
              point: false,
              present: false,
            },
          } as ReserveInputMember,
          seats: [{ seatId: '1' }] as ReserveInputSeats,
          discountCouponType: { type: 'InputtedCouponCode' },
          myCards: [] as ReserveInputCards,
          isGuest: false,
          context: { seatTypes: ['PRIVATE_ROOM'] } as ReserveInputContext,
        })
      ).toEqual({
        type: 'memberLoaded',
        seatId: '1',
        reserverNameKana: 'いっきゅうたろう',
        reserverPhoneNumber: '09012345678',
        email: 'ikyu@ikyu.com',
        paymentMethod: 'ONSITE',
        givenPointsType: 'IKYU',
        isDiscountCouponEnabled: false,
        discountCouponType: { type: 'InputtedCouponCode' },
        subscribeMailMagazineIkyuNews: true,
        subscribeMailMagazinePointCoupon: false,
      })
      expect(
        onLoadEventReserveInput({
          restaurant,
          plan,
          viewer,
          member: {
            ...member,
            mailMagazineSubscription: {
              restaurant: false,
              point: true,
              present: false,
            },
          } as ReserveInputMember,
          seats: [{ seatId: '1' }] as ReserveInputSeats,
          discountCouponType: { type: 'InputtedCouponCode' },
          myCards: [] as ReserveInputCards,
          isGuest: false,
          context: { seatTypes: ['PRIVATE_ROOM'] } as ReserveInputContext,
        })
      ).toEqual({
        type: 'memberLoaded',
        seatId: '1',
        reserverNameKana: 'いっきゅうたろう',
        reserverPhoneNumber: '09012345678',
        email: 'ikyu@ikyu.com',
        paymentMethod: 'ONSITE',
        givenPointsType: 'IKYU',
        isDiscountCouponEnabled: false,
        discountCouponType: { type: 'InputtedCouponCode' },
        subscribeMailMagazineIkyuNews: false,
        subscribeMailMagazinePointCoupon: true,
      })
      expect(
        onLoadEventReserveInput({
          restaurant,
          plan,
          viewer,
          member: {
            ...member,
            mailMagazineSubscription: {
              restaurant: true,
              point: false,
              present: true,
            },
          } as ReserveInputMember,
          seats: [{ seatId: '1' }] as ReserveInputSeats,
          discountCouponType: { type: 'InputtedCouponCode' },
          myCards: [] as ReserveInputCards,
          isGuest: false,
          context: { seatTypes: ['PRIVATE_ROOM'] } as ReserveInputContext,
        })
      ).toEqual({
        type: 'memberLoaded',
        seatId: '1',
        reserverNameKana: 'いっきゅうたろう',
        reserverPhoneNumber: '09012345678',
        email: 'ikyu@ikyu.com',
        paymentMethod: 'ONSITE',
        givenPointsType: 'IKYU',
        isDiscountCouponEnabled: false,
        discountCouponType: { type: 'InputtedCouponCode' },
        subscribeMailMagazineIkyuNews: true,
        subscribeMailMagazinePointCoupon: true,
      })
    })
  })
}

export const listenReservationInputState$ =
  import.meta.env.VITE_MODE === 'ppg'
    ? ppgListenReservationInputState$
    : ikyuListenReservationInputState$
listenReservationInputState$.debugLabel = 'listenReservationInputState$'

function initialSelectSeatId({
  seats,
  context,
}: { seats?: ReserveInputSeats; context?: ReserveInputContext }) {
  if (seats === undefined || context === undefined) {
    return undefined
  }
  // 在庫がある席が1つのみ もしくは 席の絞り込み条件が指定されていない場合は、先頭の席を選択する
  if (
    seats.length === 1 ||
    ((context.seatTypes === undefined || context.seatTypes.length === 0) &&
      context.smokingAllowed === undefined &&
      context.windowSide === undefined)
  ) {
    return seats[0].seatId
  }
  // 席の絞り込み条件に合致する席が複数ある場合は、最初に合致した席を選択する
  return seats.filter((seat) => filterByCriteria({ seat, context }))[0]?.seatId
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('initialSelectSeatId', () => {
    it('undefined', () => {
      expect(initialSelectSeatId({})).toBe(undefined)
    })
    it('在庫が1つの場合は絞り込み条件関係なく、その席を選択する', () => {
      expect(
        initialSelectSeatId({
          seats: [{ seatId: '1', seatTypeLabel: '半個室' }] as ReserveInputSeats,
          context: { seatTypes: ['PRIVATE_ROOM', 'TERRACE'] } as ReserveInputContext,
        })
      ).toBe('1')
    })
    it('席の絞り込み条件が指定されていない場合は、先頭の席を選択する', () => {
      expect(
        initialSelectSeatId({
          seats: [
            { seatId: '1', seatTypeLabel: '半個室' },
            { seatId: '2', seatTypeLabel: '完全個室' },
          ] as ReserveInputSeats,
          context: {} as ReserveInputContext,
        })
      ).toBe('1')
    })
    it('席の絞り込み条件に合致する席が複数ある場合は、最初に合致した席を選択する', () => {
      expect(
        initialSelectSeatId({
          seats: [
            { seatId: '1', seatTypeLabel: '半個室' },
            { seatId: '2', seatTypeLabel: '完全個室' },
          ] as ReserveInputSeats,
          context: { seatTypes: ['PRIVATE_ROOM', 'TERRACE'] } as ReserveInputContext,
        })
      ).toBe('2')
    })
    it('seatTypes に空配列が指定されている場合は、先頭の席を選択する', () => {
      expect(
        initialSelectSeatId({
          seats: [
            { seatId: '1', seatTypeLabel: '半個室' },
            { seatId: '2', seatTypeLabel: '完全個室' },
          ] as ReserveInputSeats,
          context: { seatTypes: [] } as unknown as ReserveInputContext,
        })
      ).toBe('1')
    })
  })
}

function filterByCriteria({
  seat,
  context,
}: { seat: ReserveInputSeat; context: ReserveInputContext }) {
  const filters = [
    context.seatTypes?.includes('PRIVATE_ROOM') && seat.seatTypeLabel === '完全個室',
    context.seatTypes?.includes('SEMI_PRIVATE_ROOM') && seat.seatTypeLabel === '半個室',
    context.seatTypes?.includes('TERRACE') && seat.seatTypeLabel === 'テラス',
    context.seatTypes?.includes('COUNTER') &&
      (seat.seatTypeLabel === 'カウンター' || seat.seatTypeLabel === 'カウンター席'),
    context.windowSide && seat.attributes.includes('WINDOW_SEAT'),
    context.smokingAllowed !== undefined && seat.smokingAllowed === context.smokingAllowed,
  ]
  return filters.some((f) => f)
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('filterByCriteria', () => {
    it('context で指定された条件にどれか一つでも合致すれば true を返す', () => {
      expect(
        filterByCriteria({
          seat: {
            seatTypeLabel: '完全個室',
            attributes: [],
          } as unknown as ReserveInputSeat,
          context: {
            seatTypes: ['PRIVATE_ROOM', 'COUNTER', 'SEMI_PRIVATE_ROOM', 'TERRACE'],
            smokingAllowed: true,
            windowSide: true,
          } as ReserveInputContext,
        })
      ).toBe(true)
    })
    it('context で指定された条件に合致しない場合は false を返す', () => {
      expect(
        filterByCriteria({
          seat: {
            seatTypeLabel: '完全個室',
            attributes: [],
          } as unknown as ReserveInputSeat,
          context: {
            seatTypes: ['SEMI_PRIVATE_ROOM', 'TERRACE', 'COUNTER'],
            smokingAllowed: true,
            windowSide: true,
          } as ReserveInputContext,
        })
      ).toBe(false)
    })
    it('完全個室', () => {
      expect(
        filterByCriteria({
          seat: { seatTypeLabel: '完全個室', attributes: [] } as unknown as ReserveInputSeat,
          context: { seatTypes: ['PRIVATE_ROOM'] } as ReserveInputContext,
        })
      ).toBe(true)
    })
    it('半個室', () => {
      expect(
        filterByCriteria({
          seat: {
            seatTypeLabel: '半個室',
            attributes: [],
          } as unknown as ReserveInputSeat,
          context: { seatTypes: ['SEMI_PRIVATE_ROOM'] } as ReserveInputContext,
        })
      ).toBe(true)
    })
    it('テラス', () => {
      expect(
        filterByCriteria({
          seat: {
            seatTypeLabel: 'テラス',
            attributes: [],
          } as unknown as ReserveInputSeat,
          context: { seatTypes: ['TERRACE'] } as ReserveInputContext,
        })
      ).toBe(true)
    })
    it('カウンター', () => {
      expect(
        filterByCriteria({
          seat: {
            seatTypeLabel: 'カウンター',
            attributes: [],
          } as unknown as ReserveInputSeat,
          context: { seatTypes: ['COUNTER'] } as ReserveInputContext,
        })
      ).toBe(true)
    })
    it('窓側', () => {
      expect(
        filterByCriteria({
          seat: {
            seatTypeLabel: 'カウンター',
            attributes: ['WINDOW_SEAT'],
          } as unknown as ReserveInputSeat,
          context: { windowSide: true } as ReserveInputContext,
        })
      ).toBe(true)
    })
    it('禁煙', () => {
      expect(
        filterByCriteria({
          seat: {
            seatTypeLabel: 'カウンター',
            attributes: [],
            smokingAllowed: false,
          } as unknown as ReserveInputSeat,
          context: { smokingAllowed: false } as ReserveInputContext,
        })
      ).toBe(true)
    })
  })
}

const effects = atom((get) => {
  get(listenInitEvent$)
  get(listenYahooMemberLinking$)
  get(listenReservationInputState$)
  // この event は discountCoupon.ts の atomEffect で発火させる
  // trustedMember.ts
  get(listenTrustedMember$)
  // trustedGuest.ts
  get(listenTrustedGuest$)
  get(listenValidateCouponQuery)
  // validate.ts
  get(listenUserInputError$)
  // reserve.ts
  get(listenReserveReady$)
  get(listenRegisteringMember$)
  get(listenCardRegistration$)
  // reserveSuccess.ts
  get(listenReserveSuccess$)
  // error.ts
  get(listenReserveError$)
})
export function useInitAtom(context: ReserveInputContext) {
  useInitScopedStore()
  useAtomValue(effects)
  const setPopupCouponDisplayed = useSetAtom(popupCouponDisplayed$)

  const setNavigationContext = useSetAtom(navigationContext$)

  useEffect(() => {
    setNavigationContext(context)
  }, [context, setNavigationContext])

  useEffect(() => {
    // Ikyu Clerk/Revisit Incentive のバッジが表示されているかをチェックする
    const badge = document.querySelector('.popup-badge, .popup-small')
    if (badge) {
      setPopupCouponDisplayed(true)
    }
  }, [setPopupCouponDisplayed])
}
